import { Heart, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEEDBACK_URL = "https://forms.gle/c2CkPKuBFrbY7zpP7";

interface FeedbackCardProps {
  /** Optional override for the headline. */
  title?: string;
  /** Optional override for the supporting copy. */
  description?: string;
  /** Optional override for the small italic footnote. */
  footnote?: string;
  /** Optional override for the CTA button label. */
  ctaLabel?: string;
  /** Extra classes for outer wrapper (e.g. margins). */
  className?: string;
}

/**
 * On-brand "Help us improve" feedback card linking to the feedback survey.
 * Uses the project's primary purple tokens so it stays consistent across the site.
 */
export const FeedbackCard = ({
  title = "Help us improve Balham Babes",
  description = "Your feedback shapes what comes next. Tell us what you love, what's missing, and what we should build.",
  footnote = "Takes 2 minutes · Your input directly guides our roadmap.",
  ctaLabel = "Share your feedback",
  className = "",
}: FeedbackCardProps) => {
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-primary/5 p-6 sm:p-8 shadow-sm ${className}`}>
      {/* soft decorative orb */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full bg-primary/15 blur-3xl"
      />

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
        <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
          <Heart className="h-6 w-6" />
        </div>

        <div className="flex-1 min-w-0 space-y-1.5">
          <h3 className="text-lg sm:text-xl font-semibold text-foreground leading-tight">
            {title}
          </h3>
          <p className="text-sm text-muted-foreground">
            {description}
          </p>
          {footnote && (
            <p className="text-xs text-muted-foreground italic pt-1">
              {footnote}
            </p>
          )}
        </div>

        <div className="shrink-0">
          <Button asChild size="lg" className="w-full sm:w-auto">
            <a href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
              {ctaLabel}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackCard;
